import React, { useState, useEffect, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

const ManifestPrint = forwardRef(({ manifestId, manifestData, companyData, printMode = 'with-header' }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [manifest, setManifest] = useState(manifestData || null);
  const [loading, setLoading] = useState(!manifestData || !companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (manifestData && companyData) {
        setManifest(manifestData);
        setCompany(companyData);
        setLoading(false);
        return;
      }

      try {
        // Fetch manifest data
        const manifestResponse = await fetch(`${backendUrl}/api/air-outward-manifests/${manifestId}`, {
          headers: getAuthHeaders()
        });
        if (manifestResponse.ok) {
          const mData = await manifestResponse.json();
          setManifest(mData);

          // Fetch company data
          const companyResponse = await fetch(`${backendUrl}/api/companies/${mData.company_id}`, {
            headers: getAuthHeaders()
          });
          if (companyResponse.ok) {
            setCompany(await companyResponse.json());
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [manifestId, manifestData, companyData, backendUrl]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const companyName = company?.name || 'COMPANY NAME';
  
  const getLogoUrl = (url) => {
    if (!url) return null;
    const fileId = url.match(/id=([^&]+)/)?.[1] || url.match(/\/d\/([^/]+)\//)?.[1];
    if (fileId) {
      const token = localStorage.getItem('auth_token');
      return `${backendUrl}/api/drive/files/${fileId}/view?token=${token}`;
    }
    return url;
  };
  const companyLogo = getLogoUrl(company?.logo_url);
  
  const companyAddress = company?.address || '';
  const companyCity = company?.city || '';
  const companyState = company?.state || '';
  const companyPincode = company?.pincode || '';
  const companyPhone = company?.phone || '';
  const companyEmail = company?.email || '';
  const companyWebsite = company?.website || '';
  const companyGst = company?.gst_number || '';
  const companyCin = company?.cin_number || '';

  const fullAddress = [companyAddress, companyCity, companyState, companyPincode].filter(Boolean).join(', ');

  // Calculate totals
  const totalGrossWeight = manifest?.packages?.reduce((sum, pkg) => {
    const weight = parseFloat(pkg.gr_weight) || 0;
    return sum + weight;
  }, 0) || 0;

  const totalChargeableWeight = manifest?.packages?.reduce((sum, pkg) => {
    const weight = parseFloat(pkg.chr_weight) || 0;
    return sum + weight;
  }, 0) || 0;

  const totalPieces = manifest?.packages?.reduce((sum, pkg) => {
    const pieces = parseInt(pkg.no_of_pieces, 10) || 0;
    return sum + pieces;
  }, 0) || 0;

  const showHeader = printMode === 'with-header';

  return (
    <>
      {/* Print Content */}
      <div
        id="manifest-print-content"
        className="bg-white print:bg-white"
        style={{ 
          fontFamily: "Arial, Helvetica, sans-serif", 
          fontSize: '11px', 
          lineHeight: '1.3'
        }}
      >
        <div
          className="p-4 print:p-3 flex flex-col"
          style={{ minHeight: '99vh' }}
        >

          {/* Header - Only if with-header mode */}
          {showHeader && (
            <>
              <div className="flex items-start justify-between mb-2 pb-2" style={{ borderBottom: '2px solid #eab308' }}>
                {/* Logo */}
                <div className="flex-shrink-0 flex items-center" style={{ width: '160px' }}>
                  {companyLogo ? (
                    <img
                      src={companyLogo}
                      alt={companyName}
                      className="w-36 h-auto object-contain"
                      style={{ maxHeight: '90px' }}
                    />
                  ) : (
                    <div className="h-16 w-20 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">
                      LOGO
                    </div>
                  )}
                </div>
                {/* Services text */}
                <div className="flex-grow text-right text-black font-semibold flex flex-col justify-end mt-2" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                  <p>Freight Forwarder</p>
                  <p>Custom Broker</p>
                  <p>Reefer Transportation</p>
                  <p>Logistics Solution</p>
                </div>
              </div>
            </>
          )}

          {/* Title */}
          <div className="text-center mb-3 mt-2">
            <h1 className="text-xl font-bold uppercase" style={{ letterSpacing: '2px' }}>CONSOLE MANIFEST</h1>
          </div>

          {/* Shipper & Consignee Section */}
          <table className="w-full border-collapse border border-black mb-2" style={{ fontSize: '10px' }}>
            <tbody>
              <tr>
                <td className="border border-black p-2 align-top" style={{ width: '15%' }}>
                  <strong>Shipper:</strong>
                </td>
                <td className="border border-black p-2">
                  <div className="font-bold">{manifest?.shipper_name || ''}</div>
                  <div className="mt-1 whitespace-pre-wrap">{(() => {
                    const name = manifest?.shipper_name || '';
                    const address = manifest?.shipper_address || '';
                    if (!address) return '';
                    let lines = address.split('\n').map(l => l.trim()).filter(l => l);
                    if (lines.length > 0 && name && lines[0].toUpperCase() === name.trim().toUpperCase()) {
                      lines.shift(); // Remove Name
                      if (lines.length >= 2 && !lines[0].includes(',') && lines[0].length < 40) {
                        lines.shift(); // Remove Nickname
                      }
                    }
                    return lines.join('\n');
                  })()}</div>
                </td>
              </tr>
              <tr>
                <td className="border border-black p-2 align-top">
                  <strong>Consignee:</strong>
                </td>
                <td className="border border-black p-2">
                  <div className="font-bold">{manifest?.consignee_name || ''}</div>
                  <div className="mt-1 whitespace-pre-wrap">{(() => {
                    const name = manifest?.consignee_name || '';
                    const address = manifest?.consignee_address || '';
                    if (!address) return '';
                    let lines = address.split('\n').map(l => l.trim()).filter(l => l);
                    if (lines.length > 0 && name && lines[0].toUpperCase() === name.trim().toUpperCase()) {
                      lines.shift(); // Remove Name
                      if (lines.length >= 2 && !lines[0].includes(',') && lines[0].length < 40) {
                        lines.shift(); // Remove Nickname
                      }
                    }
                    return lines.join('\n');
                  })()}</div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* AWB Details */}
          <table className="w-full border-collapse border border-black mb-2" style={{ fontSize: '10px', tableLayout: 'fixed' }}>
            <tbody>
              <tr>
                <td className="border border-black p-2" style={{ width: '33.33%' }}>
                  <strong>MAWB NO.:</strong> {manifest?.mawb_no || ''}
                </td>
                <td className="border border-black p-2" style={{ width: '33.33%' }}>
                  <strong>No. of HAWB:</strong> {manifest?.no_of_hawb || ''}
                </td>
                <td className="border border-black p-2" style={{ width: '33.33%' }}>
                  <strong>Freight:</strong> {manifest?.freight || ''}
                </td>
              </tr>
              <tr>
                <td className="border border-black p-2">
                  <strong>HAWB NO.:</strong> {manifest?.hawb_no || ''}
                </td>
                <td className="border border-black p-2">
                  <strong>Flight:</strong> {manifest?.flight || ''}
                </td>
                <td className="border border-black p-2">
                  <strong>Date:</strong> {formatDate(manifest?.manifest_date)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Package Details Table */}
          <table className="w-full border-collapse border border-black" style={{ fontSize: '9px' }}>
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black p-1 text-center" style={{ width: '5%' }}>Sr No.</th>
                <th className="border border-black p-1 text-center" style={{ width: '8%' }}>Gr Weight</th>
                <th className="border border-black p-1 text-center" style={{ width: '8%' }}>Chr Weight</th>
                <th className="border border-black p-1 text-left" style={{ width: '22%' }}>Shipper</th>
                <th className="border border-black p-1 text-left" style={{ width: '22%' }}>Consignee</th>
                <th className="border border-black p-1 text-left" style={{ width: '25%' }}>Commodity</th>
                <th className="border border-black p-1 text-center" style={{ width: '10%' }}>No. of Pieces</th>
              </tr>
            </thead>
            <tbody>
              {manifest?.packages && manifest.packages.length > 0 ? (
                manifest.packages.map((pkg, index) => (
                  <tr key={pkg.id || index}>
                    <td className="border border-black p-1 text-center">{index + 1}</td>
                    <td className="border border-black p-1 text-center">{pkg.gr_weight || ''}</td>
                    <td className="border border-black p-1 text-center">{pkg.chr_weight || ''}</td>
                    <td className="border border-black p-1 whitespace-pre-wrap" style={{ fontSize: '8px' }}>
                      {pkg.shipper_text || ''}
                    </td>
                    <td className="border border-black p-1 whitespace-pre-wrap" style={{ fontSize: '8px' }}>
                      {pkg.consignee_text || ''}
                    </td>
                    <td className="border border-black p-1 whitespace-pre-wrap" style={{ fontSize: '8px' }}>
                      {pkg.commodity || ''}
                    </td>
                    <td className="border border-black p-1 text-center">{pkg.no_of_pieces || ''}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="border border-black p-2 text-center text-gray-500">
                    No packages
                  </td>
                </tr>
              )}
              
              {/* Totals Row */}
              <tr className="bg-gray-100 font-bold">
                <td className="border border-black p-1 text-center"></td>
                <td className="border border-black p-1 text-center">{totalGrossWeight.toFixed(2)}</td>
                <td className="border border-black p-1 text-center">{totalChargeableWeight.toFixed(2)}</td>
                <td className="border border-black p-1" colSpan="3"></td>
                <td className="border border-black p-1 text-center">{totalPieces}</td>
              </tr>
            </tbody>
          </table>

          {/* Footer - Only if with-header mode (pinned to page bottom via mt-auto) */}
          {showHeader && (
            <div className="mt-auto pt-3" style={{ borderTop: '2px solid #eab308' }}>
              <div className="text-center text-black" style={{ fontSize: '10px', lineHeight: '1.5' }}>
                <p className="font-bold" style={{ fontSize: '11px' }}>{companyName}</p>
                {fullAddress && <p>Reg. Off: {fullAddress}</p>}
                <p>
                  {companyGst && <span className="font-semibold">GST No.: </span>}
                  {companyGst && <span>{companyGst}</span>}
                  {companyGst && companyEmail && <span className="mx-1"> </span>}
                  {companyEmail && <span className="font-semibold">Email: </span>}
                  {companyEmail && <span>{companyEmail}</span>}
                  {companyCin && <span className="mx-1"> </span>}
                  {companyCin && <span className="font-semibold">LLP IN No.: </span>}
                  {companyCin && <span>{companyCin}</span>}
                </p>
                <p>
                  {companyPhone && <span className="font-semibold">Tel: </span>}
                  {companyPhone && <span>{companyPhone}</span>}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
             size: A4;
             margin: 10mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
          }

          body * {
            visibility: hidden;
          }

          #manifest-print-content,
          #manifest-print-content * {
            visibility: visible;
          }

          #manifest-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          #manifest-print-content, #manifest-print-content * {
            font-family: Arial, Helvetica, sans-serif !important;
          }

          table {
            page-break-inside: auto;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }
        }

        @media screen {
          #manifest-print-content {
            max-width: 210mm;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>
    </>
  );
});

ManifestPrint.displayName = 'ManifestPrint';

export default ManifestPrint;
